<template>
  <BittsBaseTree
    :is-disabled="isDisabled"
    :node-data="treeData"
    :expand-all="false"
    @update="handleTreeUpdate"
  >
    <template #node-content="{ node }">
      <p class="text-neutral-text-strong text-base">{{ node.label }}</p>
    </template>
    <template #node-suffix="{ node }">
      <BittsTag
        v-if="getPopulationById(node.id)?.standard_type"
        color="info"
        text="Standard"
        size="x-small"
      />
    </template>
  </BittsBaseTree>
</template>

<script setup lang="ts">
import { BittsBaseTree, BittsTag } from '@crossbeam/bitts';
import type { TreeNodeType } from '@crossbeam/bitts/src/BittsTree/BittsBaseTree.vue';

import { computed } from 'vue';

import {
  ALL_SEGMENTS,
  Population,
  SEGMENTS_TO_LABEL_MAP,
  SegmentType,
} from '@/types/populations';

type ExtendedTreeNodeType = TreeNodeType & {
  populationData?: Population;
};

const props = withDefaults(
  defineProps<{
    populations: Population[];
    checkedPopulationIds?: number[];
    isDisabled?: boolean;
  }>(),
  {
    checkedPopulationIds: () => [],
    isDisabled: false,
  },
);

const emit = defineEmits<{
  'population-checked': [populationId: number, isChecked: boolean];
  'populations-batch-checked': [populationIds: number[], isChecked: boolean];
}>();

const populationMap = computed(() => {
  return new Map(props.populations.map((pop) => [pop.id, pop]));
});

function getPopulationById(id: number | string): Population | undefined {
  if (typeof id === 'number') {
    return populationMap.value.get(id);
  }
  return undefined;
}

const treeData = computed<ExtendedTreeNodeType[]>(() => {
  // Group populations by segment
  const populationsBySegment = props.populations.reduce(
    (acc, pop) => {
      const segment = (pop.segment as SegmentType) || 'other';
      if (!acc[segment]) {
        acc[segment] = [];
      }
      acc[segment].push(pop);
      return acc;
    },
    {} as Record<string, Population[]>,
  );

  const nodes: ExtendedTreeNodeType[] = [];
  const STANDALONE_SEGMENT_KEY = 'custom_segment_standalone';

  // Always show all standard segments, even if empty
  ALL_SEGMENTS.forEach((segment) => {
    const populations = populationsBySegment[segment] || [];
    const hasPopulations = populations.length > 0;

    const children: ExtendedTreeNodeType[] = populations.map((pop) => ({
      id: pop.id,
      label: pop.name,
      checked: props.checkedPopulationIds.includes(pop.id),
      populationData: pop,
    }));

    nodes.push({
      id: segment,
      label: SEGMENTS_TO_LABEL_MAP[segment],
      checked: hasPopulations && children.every((child) => child.checked),
      children: hasPopulations ? children : undefined,
      isDisabled: !hasPopulations,
    });
  });

  // Handle special standalone items (like Custom Populations toggle)
  const standaloneItems = populationsBySegment[STANDALONE_SEGMENT_KEY];
  if (standaloneItems && standaloneItems.length > 0) {
    standaloneItems.forEach((pop) => {
      nodes.push({
        id: pop.id,
        label: pop.name,
        checked: props.checkedPopulationIds.includes(pop.id),
        populationData: pop,
      });
    });
  }

  return nodes;
});

function handleTreeUpdate(
  updatedNodes: ExtendedTreeNodeType[],
  updatedNodeId: number | string,
) {
  // Find the changed node in the updated tree
  const changedNode = findNodeById(updatedNodes, updatedNodeId);
  if (!changedNode) return;

  // If it's a population node (has a numeric ID), emit the change
  if (typeof changedNode.id === 'number') {
    emit('population-checked', changedNode.id, changedNode.checked ?? false);
  } else {
    // If it's a segment node, emit batch change for all children
    const childIds =
      changedNode.children
        ?.filter(
          (child): child is ExtendedTreeNodeType =>
            typeof child.id === 'number',
        )
        .map((child) => child.id as number) ?? [];

    if (childIds.length > 0) {
      emit('populations-batch-checked', childIds, changedNode.checked ?? false);
    }
  }
}

function findNodeById(
  nodes: ExtendedTreeNodeType[],
  nodeId: number | string,
): ExtendedTreeNodeType | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) return node;
    if (node.children) {
      const found = findNodeById(node.children, nodeId);
      if (found) return found;
    }
  }
  return undefined;
}
</script>
